import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, FormGroup, Input, Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";

import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";

import "./CameraMissedThefts.scss";
import moment from "moment";

import { fetchCameraMissedThefts, saveCameraMissedThefts } from "../../shared/missedTheftService";
import { isTemp } from "../treeReducer";
import getOperatingModes from "../../../../services/operatingModeService";

const DRAFT_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

/** Turn a stored ISO value into the human display form (naive drafts pass through). */
function displayDate(dateTime) {
  return dateTime.replace("T", " ").replace("Z", "");
}

/**
 * A comparable key: server rows are ISO (`YYYY-MM-DDTHH:mm:ssZ`), pending drafts are naive
 * (`YYYY-MM-DD HH:mm:ss`). Normalising the date/time separator lets both sort chronologically
 * (otherwise the space `0x20` < `T` `0x54` pushes all same-day drafts below server rows).
 */
function sortKey(missedTheft) {
  return String(missedTheft.date).replace(" ", "T");
}

/** Most-recent first, non-mutating. */
function sortByDateDesc(missedThefts) {
  return [...(missedThefts || [])].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka < kb) return 1;
    if (ka > kb) return -1;
    return 0;
  });
}

/**
 * Camera-model missed thefts (view + add-only).
 *
 * Self-contained: reads the camera's missed thefts from the shared, dual-keyed
 * `/missedthefts/` endpoint (filtered by camera public_id — the camera tree does
 * NOT embed them), lets the user append new ones (date + operating mode), and
 * bulk-POSTs them keyed by camera public_id. The server cross-fills the legacy
 * `channel` side during the migration; this component never touches it.
 */
export default function CameraMissedThefts({ camera }) {
  const [modal, setModal] = useState(false);
  const [missedThefts, setMissedThefts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMissedThefts, setNewMissedThefts] = useState([]);
  const [newDateTime, setNewDateTime] = useState("");
  const [selectedMode, setSelectedMode] = useState("");
  const [selectedModeName, setSelectedModeName] = useState("");
  const [invalidInputMessage, setInvalidInputMessage] = useState("");
  const [operatingModes, setOperatingModes] = useState([]);
  const [saving, setSaving] = useState(false);
  // Monotonic token bumped on every camera change (see effect). The component instance is
  // reused across camera selections, so an in-flight save started for one camera must not
  // apply its late-resolving result to another — handleSave captures the token and bails on
  // mismatch. A token (not the public_id) also covers A→B→A, where the id would match again.
  const saveToken = useRef(0);

  // A tree-local, not-yet-persisted camera has a temp public_id (`cam_TEMP_*`). It has no
  // server row, so we must neither read nor POST against it — a POST would 400 or, worse,
  // create an orphan missed-theft keyed to a camera that does not exist server-side. The
  // camera must be saved via "Save All" first.
  const isTempCamera = isTemp(camera.public_id);

  useEffect(() => {
    // Invalidate any in-flight save from the previously selected camera.
    saveToken.current += 1;
    // Reset every per-camera field first: the component instance is reused when the user
    // selects another camera in the tree, so a leftover draft/list from the previous camera
    // must not linger (stale display) nor be POSTed against the newly selected public_id.
    setMissedThefts([]);
    setNewMissedThefts([]);
    setNewDateTime("");
    setSelectedMode("");
    setSelectedModeName("");
    setInvalidInputMessage("");
    setSaving(false);

    if (isTempCamera) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    // Reading existing records is decoupled from the operating-mode reference fetch: a modes
    // failure must degrade only the add form (empty select), never hide existing missed thefts.
    fetchCameraMissedThefts(camera.public_id)
      .then((existing) => {
        if (!cancelled) setMissedThefts(sortByDateDesc(existing));
      })
      .catch((error) => {
        console.error("Error loading camera missed thefts:", error);
        if (!cancelled) toast.error("Failed to load missed thefts.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    getOperatingModes()
      .then((response) => {
        if (!cancelled) setOperatingModes(response.data);
      })
      .catch((error) => {
        console.error("Error fetching operating modes:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [camera.public_id, isTempCamera]);

  const toggleModal = () => setModal(!modal);

  const handleDatetimeChange = (dateTime) => {
    setInvalidInputMessage("");
    if (typeof dateTime !== "string") {
      setNewDateTime(dateTime.format(DRAFT_DATE_FORMAT));
      return;
    }
    if (moment(dateTime, DRAFT_DATE_FORMAT, true).isValid()) {
      setNewDateTime(dateTime);
    } else {
      setInvalidInputMessage(`Invalid value, please follow format ${DRAFT_DATE_FORMAT}`);
      setNewDateTime("");
    }
  };

  const handleModeChange = (event) => {
    const modeId = parseInt(event.target.value, 10);
    const modeName = operatingModes.find((mode) => mode.id === modeId)?.name || "Unknown";
    setSelectedMode(modeId);
    setSelectedModeName(modeName);
  };

  const handleAddMissedTheft = () => {
    if (newDateTime === "" || selectedMode === "" || Number.isNaN(selectedMode) || invalidInputMessage !== "") {
      return;
    }
    const missedTheft = {
      date: newDateTime,
      operating_mode: selectedMode,
      operating_mode_name: selectedModeName,
      isNew: true,
    };
    setMissedThefts(sortByDateDesc([...missedThefts, missedTheft]));
    setNewMissedThefts([...newMissedThefts, missedTheft]);
    resetDraft();
  };

  const resetDraft = () => {
    setNewDateTime("");
    setSelectedMode("");
    setSelectedModeName("");
    setInvalidInputMessage("");
  };

  const handleCancel = () => {
    resetDraft();
    setNewMissedThefts([]);
    setMissedThefts((current) => sortByDateDesc(current.filter((mt) => !mt.isNew)));
    toggleModal();
  };

  const handleSave = async () => {
    const token = saveToken.current;
    const payload = newMissedThefts.map((mt) => ({
      camera: camera.public_id,
      date: mt.date,
      operating_mode: mt.operating_mode,
    }));
    setSaving(true);
    try {
      const created = await saveCameraMissedThefts(payload);
      // The user may have selected another camera while the POST was in flight. The rows are
      // persisted server-side for the original camera, but applying them here would graft them
      // onto the now-displayed camera's state — bail out of the UI update if we moved on.
      if (saveToken.current !== token) return;
      setMissedThefts((current) => sortByDateDesc([...current.filter((mt) => !mt.isNew), ...created]));
      setNewMissedThefts([]);
      resetDraft();
      toggleModal();
    } catch (error) {
      if (saveToken.current === token) toast.error("Failed to save missed thefts.");
    } finally {
      // Only the save that still owns the current token clears `saving`. A camera switch
      // already reset `saving` via the effect, so this never leaves the flag stuck; guarding
      // it also stops a stale save (camera A) from re-enabling camera B's buttons while B has
      // its own save in flight.
      if (saveToken.current === token) setSaving(false);
    }
  };

  return (
    <div className="d-inline-block">
      <Button
        id="display-camera-missed-thefts-modal"
        size="sm"
        onClick={toggleModal}
        disabled={loading || isTempCamera}
        title={isTempCamera ? "Save the camera first (Save All) to add missed thefts" : undefined}
      >
        <i className="fas fa-plus mr-2" />
        Details ({loading ? "…" : missedThefts.length})
      </Button>
      {isTempCamera && <small className="text-muted ml-2">Save the camera first</small>}
      {modal && (
        <Modal isOpen={modal} toggle={handleCancel} contentClassName="image-modal bg-transparent">
          <ModalBody
            style={{
              borderRadius: "10px",
              background: "#27293d",
              width: "700px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <center>
              <div id="camera-missed-thefts-list" style={{ maxHeight: "calc(100vh - 200px)", overflow: "auto" }}>
                {missedThefts.length > 0 ? (
                  missedThefts.map((missedTheft, index) => (
                    <p key={missedTheft.id ?? `new-${index}`} style={{ color: missedTheft.isNew ? "green" : "" }}>
                      {displayDate(missedTheft.date)} - {missedTheft.operating_mode_name || "Unknown"}
                    </p>
                  ))
                ) : (
                  <p className="text-muted">No missed thefts yet.</p>
                )}
              </div>
              {invalidInputMessage && <div style={{ color: "#ff576c", fontSize: "12px" }}>{invalidInputMessage}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
                <FormGroup style={{ marginRight: "10px", marginBottom: "0px" }}>
                  <Input
                    type="select"
                    name="operating_mode"
                    value={selectedMode}
                    onChange={handleModeChange}
                    className="custom-select"
                  >
                    <option value="">Select Operating Mode</option>
                    {operatingModes.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <DateTime
                  onChange={handleDatetimeChange}
                  value={newDateTime}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm:ss"
                  input
                />
                <Button
                  id="add-camera-missed-theft-button"
                  style={{ marginLeft: "10px", marginBottom: "2px" }}
                  onClick={handleAddMissedTheft}
                  disabled={saving}
                >
                  <i className="fas fa-plus mr-2" />
                  Add Missed Theft
                </Button>
                {newMissedThefts.length > 0 && (
                  <Button
                    id="save-camera-missed-thefts"
                    style={{ marginLeft: "10px", marginBottom: "2px" }}
                    className="btn-success"
                    onClick={handleSave}
                    name="save"
                    disabled={saving}
                  >
                    <i className="fas fa-save mr-2" />
                    Save
                  </Button>
                )}
                <Button
                  id="cancel-camera-missed-thefts"
                  style={{ marginLeft: "10px", marginBottom: "2px" }}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <i className="fas fa-times mr-2" />
                  Cancel
                </Button>
              </div>
            </center>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}

CameraMissedThefts.propTypes = {
  camera: PropTypes.shape({
    public_id: PropTypes.string.isRequired,
  }).isRequired,
};
